import React, { useState, useContext, createContext, ReactNode, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Context holds only the target DOM element
type PortalContextType = {
  targetElement: HTMLElement | null;
  setTargetElement: (element: HTMLElement | null) => void;
} | null;

// Create the context
const PortalContext = createContext<PortalContextType>(null);

// Provider props
interface PortalProviderProps {
  children: ReactNode;
}

// Provider component
function PortalProvider({ children }: PortalProviderProps) {
  // Single state to track the target element
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  // Provide a stable context value
  const contextValue = React.useMemo(
    () => ({
      targetElement,
      setTargetElement,
    }),
    [targetElement],
  );

  return <PortalContext.Provider value={contextValue}>{children}</PortalContext.Provider>;
}

// Portal anchor component
function PortalAnchor({ className }: { className?: string }) {
  const context = useContext(PortalContext);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  if (!context) {
    throw new Error('PortalAnchor must be used within a PortalProvider');
  }

  // Set the anchor element in context when it's created
  useEffect(() => {
    if (anchorRef.current && context.targetElement !== anchorRef.current) {
      context.setTargetElement(anchorRef.current);
    }

    // Clean up when unmounted
    return () => {
      if (context.targetElement === anchorRef.current) {
        context.setTargetElement(null);
      }
    };
  }, [context]);

  return <div ref={anchorRef} className={className} data-portal-anchor="true" />;
}

// Portal component props
interface PortalProps {
  children: ReactNode;
}

// Portal component
function Portal({ children }: PortalProps) {
  const context = useContext(PortalContext);

  if (!context) {
    throw new Error('Portal must be used within a PortalProvider');
  }

  // If no target element exists, don't render anything
  if (!context.targetElement) {
    return null;
  }

  // Use React's createPortal to render children into the target element
  return createPortal(children, context.targetElement);
}

export { PortalProvider, Portal, PortalAnchor };
